import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, Plus, School, Sparkles, UsersRound } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import BoothAdminCard from '../../../components/booth/BoothAdminCard';
import ReflectStaffShell, {
  StaffError,
  StaffPageLoading,
  staffRequest,
} from '../../../components/reflect/staff/ReflectStaffShell';
import {
  StaffPanel,
  StaffSwitch,
} from '../../../components/reflect/staff/StaffControls';
import { useAuth } from '../../../contexts/AuthContext';
import { isReflectLead } from '../../../utils/reflectStaff';

const fieldClasses = [
  'mt-2 min-h-12 w-full rounded-xl border border-white/15 bg-black/30 px-4 text-white',
  'outline-none placeholder:text-white/30 focus:border-cyan focus:ring-2 focus:ring-cyan/25',
].join(' ');

const gradeLabel = (band) => band === 'high_school' ? 'High school' : 'Middle school';

const rosterCounts = (roster = []) => roster.reduce(
  (counts, student) => {
    if (student.status === 'pending') counts.pending += 1;
    if (student.status === 'unclaimed') counts.unclaimed += 1;
    if (student.status === 'approved') counts.approved += 1;
    return counts;
  },
  { pending: 0, unclaimed: 0, approved: 0 },
);

const ClassCard = ({ classroom }) => {
  const counts = rosterCounts(classroom.roster);
  const created = new Date(classroom.created_at);

  return (
    <Link
      to={`/admin/reflect/classes/${classroom.id}`}
      className="group block rounded-[1.5rem] border border-white/10 bg-[#0d1011]/95 p-5
        transition hover:border-cyan/45 hover:shadow-[0_18px_55px_-35px_rgba(0,255,221,0.7)]
        focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-heading text-xl font-black">{classroom.name}</h2>
            {Boolean(classroom.is_practice) && (
              <span className="rounded-full border border-white/20 bg-white/[0.06] px-2.5 py-1 text-xs font-black">
                Practice
              </span>
            )}
          </div>
          <p className="mt-1 text-sm font-bold text-white/50">
            {gradeLabel(classroom.grade_band)}
            {classroom.site_name ? ` · ${classroom.site_name}` : ''}
          </p>
        </div>
        <ArrowRight className="mt-1 h-5 w-5 text-white/35 transition group-hover:translate-x-1 group-hover:text-cyan" />
      </div>
      <div className="mt-5 flex flex-wrap items-end justify-between gap-4 border-t border-white/10 pt-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-white/35">Join code</p>
          <p className="mt-1 font-heading text-2xl font-black tracking-[0.22em] text-cyan">
            {classroom.join_code}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs font-bold">
          <span className="rounded-full border border-cyan/35 bg-cyan/10 px-3 py-1.5 text-cyan">
            {counts.pending} waiting
          </span>
          <span className="rounded-full border border-white/15 px-3 py-1.5 text-white/60">
            {counts.unclaimed} not claimed
          </span>
          <span className="rounded-full border border-white/20 bg-white/[0.06] px-3 py-1.5">
            {counts.approved} approved
          </span>
        </div>
      </div>
      <p className="mt-4 text-xs text-white/35">
        Created {Number.isNaN(created.getTime()) ? 'recently' : created.toLocaleDateString()}
      </p>
    </Link>
  );
};

const ReflectClasses = () => {
  const { user } = useAuth();
  const lead = isReflectLead(user);
  const [classes, setClasses] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [reflectLive, setReflectLive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    grade_band: 'middle_school',
    site_name: '',
    is_practice: false,
    auto_approve: false,
  });
  const navigate = useNavigate();

  const loadClasses = useCallback(async () => {
    setError('');
    try {
      const requests = [staffRequest('/api/reflect/classes'), staffRequest('/api/reflect/alerts')];
      if (lead) requests.push(staffRequest('/api/reflect/settings/live'));
      const [classList, alertList, liveSetting] = await Promise.all(requests);
      const detailed = await Promise.all(classList.map(async (classroom) => {
        try {
          return await staffRequest(`/api/reflect/classes/${classroom.id}`);
        } catch (_error) {
          return { ...classroom, roster: [] };
        }
      }));
      setClasses(detailed);
      setAlerts(alertList);
      if (liveSetting) setReflectLive(Boolean(liveSetting.reflect_live));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [lead]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  const updateForm = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const createClass = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await staffRequest('/api/reflect/classes', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setForm({ name: '', grade_band: 'middle_school', site_name: '', is_practice: false, auto_approve: false });
      await loadClasses();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  // One tap for staff demos and trainings: a practice class (wiped nightly) with guest
  // names and auto-approve, so nobody waits on an approval during a demo.
  const startDemo = async () => {
    setBusy(true);
    setError('');
    try {
      const today = new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      const created = await staffRequest('/api/reflect/classes', {
        method: 'POST',
        body: JSON.stringify({
          name: `Demo, ${today}`,
          grade_band: form.grade_band,
          site_name: '',
          is_practice: true,
          auto_approve: true,
        }),
      });
      for (let number = 1; number <= 8; number += 1) {
        await staffRequest(`/api/reflect/classes/${created.id}/roster`, {
          method: 'POST',
          body: JSON.stringify({ display_name: `Guest ${number}` }),
        });
      }
      navigate(`/admin/reflect/classes/${created.id}`);
    } catch (requestError) {
      setError(requestError.message);
      setBusy(false);
    }
  };

  const changeLive = async (nextValue) => {
    if (nextValue) {
      const confirmed = window.confirm(
        'Make sure the consent form, care-alert protocol and data terms are done. Turn Reflect live on?',
      );
      if (!confirmed) return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await staffRequest('/api/reflect/settings/live', {
        method: 'PUT',
        body: JSON.stringify({ reflect_live: nextValue }),
      });
      setReflectLive(Boolean(result.reflect_live));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const sortedClasses = useMemo(() => classes, [classes]);

  return (
    <ReflectStaffShell
      title="Your Reflect classes"
      subtitle="Set up the room, approve students, and keep the session moving."
      alerts={alerts}
    >
      <StaffError>{error}</StaffError>

      {lead && (
        <>
          <StaffPanel className="mb-6 border-cyan/25 bg-cyan/[0.04]">
            <StaffSwitch
              checked={reflectLive}
              disabled={busy}
              onChange={changeLive}
              label="Reflect live"
              description="When off, only practice classes can join. Turn this on only when the program is ready."
            />
          </StaffPanel>
          <BoothAdminCard />
        </>
      )}

      {loading ? (
        <StaffPageLoading line="Loading your classes..." />
      ) : (
        <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_24rem]">
          <section aria-labelledby="class-list-title">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 id="class-list-title" className="font-heading text-xl font-black">Classes</h2>
              <span className="text-sm font-bold text-white/40">
                {classes.length} {classes.length === 1 ? 'class' : 'classes'}
              </span>
            </div>
            {sortedClasses.length ? (
              <div className="grid gap-4 lg:grid-cols-2">
                {sortedClasses.map((classroom) => (
                  <ClassCard key={classroom.id} classroom={classroom} />
                ))}
              </div>
            ) : (
              <StaffPanel className="flex min-h-64 flex-col items-center justify-center text-center">
                <img src="/pizzul/construction.png" alt="Pizzul ready to build" className="h-28 w-28 object-contain" />
                <h2 className="mt-3 font-heading text-2xl font-black">Build your first class</h2>
                <p className="mt-2 max-w-md text-white/55">Use the form to create a practice room or a live class.</p>
              </StaffPanel>
            )}
          </section>

          <StaffPanel as="aside" className="xl:sticky xl:top-6">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan/10 text-cyan">
                <Plus className="h-5 w-5" />
              </span>
              <div>
                <h2 className="font-heading text-xl font-black">New class</h2>
                <p className="text-sm text-white/45">A fresh roster and join code.</p>
              </div>
            </div>
            <Button
              type="button"
              onClick={startDemo}
              disabled={busy}
              className="mt-6 min-h-12 w-full rounded-full border border-cyan/40
                bg-cyan/10 font-black text-cyan hover:bg-cyan/20"
            >
              <Sparkles className="mr-2 h-5 w-5" />
              {busy ? 'Setting up...' : 'Start a demo class'}
            </Button>
            <p className="mt-2 text-center text-xs font-semibold text-white/45">
              Practice class with 8 guest names. No approvals, wiped tonight.
            </p>
            <form onSubmit={createClass} className="mt-6 space-y-5">
              <label className="block font-bold">
                Class name
                <input
                  value={form.name}
                  onChange={(event) => updateForm('name', event.target.value)}
                  required
                  maxLength={100}
                  placeholder="Ms. Rivera's 7th grade"
                  className={fieldClasses}
                />
              </label>
              <label className="block font-bold">
                Grade band
                <select
                  value={form.grade_band}
                  onChange={(event) => updateForm('grade_band', event.target.value)}
                  className={fieldClasses}
                >
                  <option value="middle_school">Middle school</option>
                  <option value="high_school">High school</option>
                </select>
              </label>
              <label className="block font-bold">
                <span className="flex items-center gap-2">
                  <School className="h-4 w-4 text-white/45" />
                  Site or school name
                </span>
                <input
                  value={form.site_name}
                  onChange={(event) => updateForm('site_name', event.target.value)}
                  maxLength={120}
                  placeholder="Optional"
                  className={fieldClasses}
                />
                <span className="mt-2 block text-xs font-semibold leading-relaxed text-white/45">
                  Used to scrub the site or school name from AI requests.
                </span>
              </label>
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <StaffSwitch
                  checked={form.is_practice}
                  onChange={(value) => updateForm('is_practice', value)}
                  label="Practice class"
                  description="Practice classes are wiped every night."
                />
                {form.is_practice && (
                  <div className="mt-4 border-t border-white/10 pt-4">
                    <StaffSwitch
                      checked={form.auto_approve}
                      onChange={(value) => updateForm('auto_approve', value)}
                      label="Auto-approve students"
                      description="Good for demos and staff practice. Real classes always need your approval."
                    />
                  </div>
                )}
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="min-h-12 w-full rounded-full bg-cyan font-black text-black hover:bg-cyan/90"
              >
                <UsersRound className="mr-2 h-5 w-5" />
                {busy ? 'Creating...' : 'Create class'}
              </Button>
            </form>
          </StaffPanel>
        </div>
      )}
    </ReflectStaffShell>
  );
};

export default ReflectClasses;
